import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, FolderKanban, ShieldCheck, TrendingUp, CheckSquare,
  Truck, Gift, SlidersHorizontal, ScrollText, Megaphone, LifeBuoy, FileBarChart,
  MessageSquare, BookOpen, Sparkles, Search, Bell, LogOut, ChevronDown, Menu, X, Repeat,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const NAV = [
  { section: "Overview", items: [
    { to: "/", label: "Command Centre", icon: LayoutDashboard, tid: "nav-command" },
  ]},
  { section: "Partner & Project", items: [
    { to: "/partners", label: "Partners", icon: Users, tid: "nav-partners" },
    { to: "/projects", label: "Projects", icon: FolderKanban, tid: "nav-projects" },
    { to: "/verification", label: "Verification Desk", icon: ShieldCheck, tid: "nav-verification" },
  ]},
  { section: "Sales & Distribution", items: [
    { to: "/sales", label: "Sales CRM", icon: TrendingUp, tid: "nav-sales" },
    { to: "/tasks", label: "Tasks & Follow-ups", icon: CheckSquare, tid: "nav-tasks" },
    { to: "/distributor", label: "Distributor Portal", icon: Truck, tid: "nav-distributor" },
  ]},
  { section: "Loyalty & Admin", items: [
    { to: "/loyalty", label: "Loyalty & Rewards", icon: Gift, tid: "nav-loyalty" },
    { to: "/studio", label: "Program Studio", icon: SlidersHorizontal, tid: "nav-studio" },
    { to: "/audit", label: "Audit Trail", icon: ScrollText, tid: "nav-audit" },
  ]},
  { section: "Priority 2 · Scaffolded", items: [
    { to: "/campaigns", label: "Campaigns & Events", icon: Megaphone, tid: "nav-campaigns" },
    { to: "/support", label: "Support & Helpdesk", icon: LifeBuoy, tid: "nav-support" },
    { to: "/reports", label: "Reporting & Export", icon: FileBarChart, tid: "nav-reports" },
    { to: "/comms", label: "Communication Centre", icon: MessageSquare, tid: "nav-comms" },
    { to: "/library", label: "Technical Library", icon: BookOpen, tid: "nav-library" },
  ]},
  { section: "Priority 3 · Future", items: [
    { to: "/ai", label: "AI Intelligence", icon: Sparkles, tid: "nav-ai", future: true },
  ]},
];

function ClBadge() {
  return (
    <div className="cl-badge flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-slate-200" data-testid="powered-by-badge">
      <span className="h-1.5 w-1.5 rounded-full bg-blue-400 pulse-dot" />
      Powered by <span className="font-semibold text-white">ChannelLoyalty.ai</span>
    </div>
  );
}

export default function Layout({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, boot, logout, loginDemo, refreshBoot } = useAuth();
  const [demos, setDemos] = useState([]);
  const [notes, setNotes] = useState([]);
  const [openNotes, setOpenNotes] = useState(false);
  const [openRole, setOpenRole] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch {}
  }, []);

  useEffect(() => {
    api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {});
    loadNotes();
  }, [loadNotes]);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const runSearch = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults(null); return; }
    try {
      const { data } = await api.get("/search", { params: { q: val } });
      setResults(data);
    } catch {}
  };

  const goto = (path) => { setResults(null); setQ(""); nav(path); };

  const markRead = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    loadNotes();
    refreshBoot?.();
  };

  const unread = notes.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen flex bg-[var(--canvas)]">
      {/* Sidebar */}
      <aside className={`navy-grad fixed z-40 lg:static top-0 left-0 h-full w-[268px] flex-col overflow-y-auto transition-transform ${mobileOpen ? "flex translate-x-0" : "hidden lg:flex -translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-4 flex items-center gap-2.5 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg grid place-items-center font-extrabold text-white copper-glow" style={{ background: "var(--copper)" }}>IN</div>
          <div>
            <p className="text-white font-display font-bold leading-tight">INDIGO</p>
            <p className="text-[10px] text-slate-400 tracking-wide">Partner Network</p>
          </div>
          <button className="ml-auto lg:hidden text-slate-300" onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>
        <nav className="px-3 py-4 flex-1">
          {NAV.map((grp) => (
            <div key={grp.section} className="mb-4">
              <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{grp.section}</p>
              {grp.items.map((it) => {
                const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
                const Icon = it.icon;
                return (
                  <div key={it.to} className={`nav-link ${active ? "active" : ""}`} data-testid={it.tid} onClick={() => nav(it.to)}>
                    <Icon size={17} className="nav-ico" />
                    <span className="flex-1">{it.label}</span>
                    {it.future && <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" title="Future Integration" />}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10">
          <ClBadge />
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-[var(--navy-800)] text-white">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></button>

            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Search partners, projects, distributors…"
                className="w-full bg-white/10 border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[var(--copper)]"
              />
              {results && (
                <div className="absolute mt-2 w-full bg-white rounded-lg shadow-xl border border-slate-200 text-slate-800 max-h-96 overflow-auto z-50" data-testid="search-results">
                  {["partners", "projects", "distributors"].map((k) =>
                    results[k]?.length ? (
                      <div key={k} className="p-2">
                        <p className="px-2 text-[10px] font-bold uppercase text-slate-400">{k}</p>
                        {results[k].map((r) => (
                          <button key={r.id} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm"
                            onClick={() => goto(k === "partners" ? `/partners/${r.id}` : k === "projects" ? `/projects/${r.id}` : "/distributor")}>
                            {r.name} <span className="text-slate-400 text-xs">{r.partner_id || r.project_id || r.city}</span>
                          </button>
                        ))}
                      </div>
                    ) : null
                  )}
                  {!results.partners?.length && !results.projects?.length && !results.distributors?.length && (
                    <p className="p-4 text-sm text-slate-400">No matches</p>
                  )}
                </div>
              )}
            </div>

            <div className="hidden sm:block"><ClBadge /></div>

            {/* Role switcher */}
            <div className="relative">
              <button data-testid="demo-role-switcher" onClick={() => setOpenRole((v) => !v)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-3 py-2 text-sm">
                <Repeat size={14} className="text-[var(--copper)]" />
                <span className="hidden sm:inline max-w-[130px] truncate">{user?.sub_role || user?.role}</span>
                <ChevronDown size={14} />
              </button>
              {openRole && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 text-slate-800 z-50 p-2" data-testid="role-switcher-menu">
                  <p className="px-2 py-1 text-[10px] font-bold uppercase text-slate-400">Switch demo persona</p>
                  {demos.map((d) => (
                    <button key={d.email} data-testid={`role-option-${(d.sub_role || d.role).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm"
                      onClick={async () => { setOpenRole(false); await loginDemo(d.email); nav("/"); }}>
                      <span className="font-medium">{d.sub_role}</span>
                      <span className="block text-xs text-slate-400">{d.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="relative">
              <button data-testid="notifications-btn" onClick={() => { setOpenNotes((v) => !v); }} className="relative p-2 hover:bg-white/10 rounded-lg">
                <Bell size={18} />
                {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-[var(--copper)] text-[10px] grid place-items-center font-bold">{unread}</span>}
              </button>
              {openNotes && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 text-slate-800 z-50 max-h-96 overflow-auto" data-testid="notifications-panel">
                  <div className="px-4 py-2.5 border-b border-slate-100 font-semibold text-sm">Notifications</div>
                  {notes.length === 0 && <p className="p-4 text-sm text-slate-400">No notifications</p>}
                  {notes.map((n) => (
                    <button key={n.id} onClick={() => markRead(n)} className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${!n.read ? "bg-orange-50/40" : ""}`}>
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500">{n.body}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{fmtDateTime(n.timestamp)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User */}
            <div className="flex items-center gap-2 pl-2 border-l border-white/15">
              <div className="h-8 w-8 rounded-full bg-[var(--copper)] grid place-items-center text-xs font-bold">
                {user?.name?.split(" ").map((x) => x[0]).slice(0, 2).join("")}
              </div>
              <button data-testid="logout-btn" onClick={logout} className="p-2 hover:bg-white/10 rounded-lg" title="Logout"><LogOut size={16} /></button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
